<div class="activity-panel">

    <!-- ======================= Success banner ======================= -->
    <div class="success-banner" *ngIf="successBanner as banner">
        <i class="ph-bold ph-check-circle banner-icon"></i>
        <span class="banner-msg">{{ banner.message }}</span>
        <button type="button" class="banner-view" (click)="onBannerView()">View →</button>
        <button type="button"
                class="banner-close"
                aria-label="Dismiss"
                (click)="dismissBanner()">
            <i class="ph-bold ph-x"></i>
        </button>
    </div>

    <!-- ======================= Tasks (option B: long deliverables) ======================= -->
    <div class="tasks-section" *ngIf="tasks.length > 0">
        <div class="rev-header">
            <i class="ph-bold ph-list-checks"></i>
            <span class="rev-header-title">Tasks</span>
            <span class="spacer"></span>
        </div>
        <div class="task-row" *ngFor="let task of tasks; trackBy: trackByTaskKey">
            <app-gable-deliverable-card
                *ngIf="task.state === 'running'"
                [loading]="true"
                [loadingLabel]="task.title"
                [loadingHint]="task.slow ? 'Taking longer than usual — still working; it will appear here when ready.' : 'Running in the background — you can keep working.'">
            </app-gable-deliverable-card>

            <app-gable-deliverable-card
                *ngIf="task.state === 'ready'"
                [payload]="deliverablePayload(task)">
            </app-gable-deliverable-card>

            <div class="task-failed" *ngIf="task.state === 'failed'">
                <i class="ph-bold ph-warning-circle"></i>
                <span class="task-failed-msg">{{ task.error || 'This analysis could not be completed.' }}</span>
                <button type="button" class="task-dismiss" (click)="dismissTask(task)" aria-label="Dismiss">
                    <i class="ph-bold ph-x"></i>
                </button>
            </div>
        </div>
    </div>

    <!-- ======================= Needs your review ======================= -->
    <div class="rev-header">
        <i class="ph-bold ph-info" [class.has-items]="waitingCount > 0"></i>
        <span class="rev-header-title">Needs your review</span>
        <span class="spacer"></span>
    </div>

    <!-- Filter chips — directly under the heading, filtering queue + feed -->
    <div class="filters">
        <button *ngFor="let filter of visibleFilters; trackBy: trackByFilterKey"
                type="button"
                class="fchip"
                [class.on]="activeFilter === filter.key"
                [attr.aria-pressed]="activeFilter === filter.key"
                (click)="setFilter(filter.key)">
            {{ filter.label }}
        </button>
    </div>

    <ng-container *ngIf="!queueEmpty; else queueEmptyTpl">
        <div *ngFor="let group of reviewGroups; trackBy: trackByGroupModule"
             class="rev-group">

            <!-- Module group header -->
            <div class="rev-group-hd">
                <i class="ph-bold rev-group-icon"
                   [ngClass]="groupIcon(group)"
                   [class.rev-group-icon-requests]="group.module === 'requests'"></i>
                <span class="rev-group-name">{{ group.label }}</span>
                <span class="rev-group-count">{{ group.entries.length }}</span>
                <span class="spacer"></span>
                <!-- v1: "Approve all" removed from the UI (every suggestion is
                     reviewed one at a time). The handler + sequential-commit
                     logic below are kept (marked @deprecated) so this can be
                     re-enabled without a rebuild. To restore, uncomment:
                <button type="button"
                        class="btn-approve-all"
                        *ngIf="groupHasPendingTyped(group)"
                        [disabled]="approvingGroup !== null"
                        (click)="approveAllInGroup(group)">
                    <i class="ph-bold ph-checks"></i>
                    {{ approvingGroup === group.module ? 'Approving…' : 'Approve all' }}
                </button>
                -->
            </div>

            <div class="rev-items">
              <!-- Suggestions for the SAME request are clustered together (SD-9015). A cluster of
                   one renders exactly as before — no sub-header, no extra nesting. -->
              <div *ngFor="let cluster of group.clusters; trackBy: trackByClusterKey"
                   class="rev-cluster"
                   [class.rev-cluster-grouped]="cluster.subHeader">

                <div class="rev-cluster-hd" *ngIf="cluster.subHeader">
                    <span class="rev-cluster-name">{{ cluster.label }}</span>
                    <span class="rev-cluster-count">{{ cluster.entries.length }} suggestions</span>
                </div>

                <div *ngFor="let entry of cluster.entries; trackBy: trackByProposalId"
                     class="rev-item"
                     [class.resolved]="entry.resolvedLabel"
                     [class.open]="isExpanded(entry)">

                    <!-- Typed-action proposals: collapsed accordion row → full card -->
                    <ng-container *ngIf="entry.cardPayload as payload">
                        <!-- Figma: no leading icon tile on queue rows; caret points
                             right and rotates down when the row expands. -->
                        <button type="button"
                                class="rev-collapsed"
                                [attr.aria-expanded]="isExpanded(entry)"
                                (click)="toggleExpanded(entry)">
                            <div class="rev-main">
                                <div class="rev-collapsed-top">
                                    <span class="rev-title">{{ entryTitle(entry) }}</span>
                                    <span class="rev-amount" *ngIf="entryAmount(entry) as amt">{{ amt }}</span>
                                </div>
                                <div class="rev-sub">{{ entrySummary(entry) }}</div>
                            </div>
                            <i class="ph-bold ph-check rev-collapsed-done" *ngIf="entryResolved(entry)"></i>
                            <i class="ph-bold ph-caret-right evidence-caret" *ngIf="!entryResolved(entry)"></i>
                        </button>

                        <div class="rev-action-card" *ngIf="isExpanded(entry)">
                            <app-gable-action-preview-card [payload]="payload"
                                                           [hideHeaderSection]="true"
                                                           (confirm)="onCardConfirm(entry, $event)"
                                                           (cancel)="onCardCancel(entry, $event)"
                                                           (snooze)="onCardSnooze(entry, $event)"
                                                           (utilityAction)="onCardUtility(entry, $event)">
                            </app-gable-action-preview-card>
                        </div>
                    </ng-container>

                    <!-- Legacy CRUD proposals: summary row + approve/reject -->
                    <div class="rev-row" *ngIf="entry.cardPayload === null">
                        <div class="domain-tile" [ngClass]="domainToneClass(entry.proposal.actionType)">
                            <i class="ph-bold" [ngClass]="domainIcon(entry.proposal.actionType)"></i>
                        </div>
                        <div class="rev-main">
                            <div class="rev-title">{{ entry.proposal.summary }}</div>
                            <div class="rev-sub">Expires {{ entry.proposal.expiresAt | date:'shortTime' }}</div>

                            <div class="rev-acts" *ngIf="!entry.resolvedLabel">
                                <button type="button"
                                        class="btn-sm btn-primary"
                                        [disabled]="entry.busy"
                                        (click)="approveCrud(entry)">
                                    <i class="ph-bold ph-check"></i>
                                    Approve
                                </button>
                                <button type="button"
                                        class="btn-sm btn-secondary"
                                        [disabled]="entry.busy"
                                        (click)="rejectCrud(entry)">
                                    Reject
                                </button>
                            </div>

                            <div class="rev-resolved-note" *ngIf="entry.resolvedLabel">
                                <i class="ph-bold ph-check"></i>
                                {{ entry.resolvedLabel }} — done
                            </div>
                        </div>
                    </div>
                </div>
              </div>
            </div>
        </div>
    </ng-container>

    <ng-template #queueEmptyTpl>
        <div class="queue-empty">
            <i class="ph-bold ph-check-circle"></i>
            <div class="empty-title">All caught up</div>
            <div class="empty-sub">Nothing needs you right now — new suggestions will appear here.</div>
        </div>
    </ng-template>

    <!-- Feed load failure — distinct from the empty state, with a retry. -->
    <div class="feed-card" *ngIf="feedError">
        <div class="queue-empty neutral">
            <i class="ph-bold ph-warning-circle"></i>
            <div class="empty-title">Couldn't load activity</div>
            <div class="empty-sub">Something went wrong fetching the feed.</div>
            <button type="button" class="btn-sm btn-secondary" (click)="retryFeed()">Retry</button>
        </div>
    </div>

    <!-- ======================= Module-grouped feed ======================= -->
    <ng-container *ngIf="feedDays.length; else feedEmptyTpl">
        <ng-container *ngFor="let day of feedDays; trackBy: trackByDayLabel">
            <div class="feed-grp">{{ day.label }}</div>
            <div class="feed-card">
                <div *ngFor="let item of day.items; trackBy: trackByItemId"
                     class="feed-item">
                    <div class="domain-tile" [ngClass]="domainToneClass(item.actionType)">
                        <i class="ph-bold" [ngClass]="domainIcon(item.actionType)"></i>
                    </div>
                    <div class="feed-main">
                        <div class="feed-title">{{ item.summary }}</div>
                        <div class="feed-meta">{{ decidedTime(item) }}</div>
                        <div class="feed-reason" *ngIf="dismissReasonLabel(item) as reason">
                            Reason: {{ reason }}
                        </div>
                    </div>
                    <div class="feed-side">
                        <span class="pill" [ngClass]="statusPillClass(item)">
                            {{ statusLabel(item) }}
                        </span>
                    </div>
                </div>
            </div>
        </ng-container>

        <div class="feed-more" *ngIf="canLoadMore">
            <button type="button"
                    class="btn-sm btn-secondary"
                    [disabled]="feedLoading"
                    (click)="loadMore()">
                {{ feedLoading ? 'Loading…' : 'Load more' }}
            </button>
        </div>
    </ng-container>

    <ng-template #feedEmptyTpl>
        <div class="feed-card" *ngIf="feedLoaded && !feedError">
            <div class="queue-empty neutral">
                <i class="ph-bold ph-clock"></i>
                <div class="empty-title">{{ feedEmptyLabel }}</div>
                <div class="empty-sub">Actions you confirm or dismiss will appear here.</div>
            </div>
        </div>
    </ng-template>

</div>
